import { clsx } from "clsx";
import dayjs, { Dayjs } from "dayjs";

import { DateFormat, Zone, ZoneTZIndependant } from "../../../services/TimeService/TimeService";
import { DisabledTime } from "../RVDatePickerDayJSFacade/utils/DisabledTimes/DisabledTimes";
import { RVTimePicker } from "../RVTimePicker/RVTimePicker";

import type { SizeType } from "antd/lib/config-provider/SizeContext";

import styles from "./RVDateTimePicker.module.scss";

export interface DateTimePickerProps {
    /**
     * Callback when the value changes. Receives ISO string or undefined.
     */
    onChange?: (value: string | undefined) => void;
    /**
     * Current value (ISO string, Dayjs, or undefined)
     */
    value?: any;
    /**
     * Placeholder text for the input
     */
    placeholder?: string;
    /**
     * Size of the picker
     */
    size?: SizeType;
    /**
     * Additional className for styling
     */
    className?: string;
    /**
     * Timezone to use for display/formatting
     */
    timezone?: Zone | ZoneTZIndependant;
    /**
     * Display format string
     */
    displayFormat?: DateFormat;
    /**
     * If true, the picker will be disabled
     */
    disabled?: boolean;
    /**
     * If true, output value in UTC
     */
    useUTCValue?: boolean;
    /**
     * Function to disable certain dates
     */
    disabledDate?: (date: Dayjs) => boolean;
    /**
     * Function to disable certain times
     */
    disabledTime?: DisabledTime<Dayjs>;
}

/**
 * Regal Voice DateTime Picker component using Dayjs and Ant Design.
 *
 * @remarks
 * This is a styled wrapper for picking both date and time, using Dayjs for date management.
 * Use this for all date-time picking needs in the app for consistency.
 *
 * @param props - See DateTimePickerProps
 */
export function RVDateTimePicker({
    size = "large",
    value,
    onChange,
    placeholder,
    className = "",
    disabled,
    timezone,
    displayFormat,
    useUTCValue = false,
    disabledDate,
    disabledTime,
}: DateTimePickerProps): JSX.Element {
    const tz = timezone ?? ZoneTZIndependant.et;
    const fmt = displayFormat ?? DateFormat.PRETTY_SHORT_TIME_DEFAULT;
    const dateValue = value ? dayjs(value).tz(tz) : null;
    function onDateChange(newDate: Dayjs | null) {
        let value;
        if (newDate) {
            value = useUTCValue
                ? dayjs(newDate).tz(tz).utc().format(DateFormat.ISO_8601)
                : dayjs(newDate).tz(tz).format(DateFormat.ISO_8601);
        }
        onChange?.(value);
    }
    return (
        // TODO: udate this to be RVDayJSJImpl
        <RVTimePicker
            data-testid="datetimepicker-testid"
            picker={undefined}
            placeholder={placeholder}
            format={DateFormat.DATE_TIME_TZ_INDEPENDENT}
            onChange={onDateChange}
            onSelect={onDateChange}
            value={dateValue}
            disabled={disabled}
            size={size}
            className={clsx(className, styles.dateTimePicker)}
            showNow={false}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            showTime={{
                format: fmt,
                use12Hours: true,
            }}
            disabledDate={disabledDate}
            disabledTime={disabledTime}
        />
    );
}
